import protobufjs from "protobufjs";
import { NetMessageCodec } from "@/websocket/codec";
import * as telegramProto from "@/api/telegram";

export interface RetryEventDetails {
  readonly retries: number;
  readonly backoff: number;
}

export enum WebsocketEvents {
  open = "open",
  close = "close",
  error = "error",
  retry = "retry",
}

interface WebsocketEventMap {
  open: Event;
  close: CloseEvent;
  error: Event;
  retry: CustomEvent<RetryEventDetails>;
}

type eventListener<K extends WebsocketEvents> = {
  readonly listener: (instance: WebsocketEx, ev: WebsocketEventMap[K]) => any;
  readonly options?: boolean | EventListenerOptions;
};

type WebsocketEventListeners = {
  open: eventListener<WebsocketEvents.open>[];
  close: eventListener<WebsocketEvents.close>[];
  error: eventListener<WebsocketEvents.error>[];
  retry: eventListener<WebsocketEvents.retry>[];
};

export interface WebsocketMessageEvent {
  readonly main: number;
  readonly sub: number;
  readonly payload: any;
}

type WebsocketMessageListener = {
  readonly listener: (instance: WebsocketEx, ev: any) => any;
  readonly proto?: any | undefined;
};

type WebsocketMessageListeners = Map<string, WebsocketMessageListener[]>;

export class WebsocketEx {
  private readonly url: string;
  private readonly protocols?: string | string[];
  private closedByUser = false;
  private retries = 0;

  private readonly codec: NetMessageCodec;
  private websocket?: WebSocket;

  private protoRoot;

  private readonly eventListeners: WebsocketEventListeners = {
    open: [],
    close: [],
    error: [],
    retry: [],
  };

  private readonly messageListeners: WebsocketMessageListeners = new Map();

  constructor(url: string, protocols?: string | string[]) {
    this.codec = new NetMessageCodec();
    this.url = url;
    this.protocols = protocols;
    //this.loadProto();
    this.protoRoot = telegramProto; // 使用编译后的JS对象
    this.tryConnect();
  }

  get underlyingWebsocket(): WebSocket | undefined {
    return this.websocket;
  }

  private async loadProto(): Promise<void> {
    try {
      //this.protoRoot = await protobufjs.load("@/api/telegram.proto");
      this.protoRoot = await protobufjs.load("./src/api/telegram.proto");
      console.log("Successfully loaded .proto file");
    } catch (err) {
      console.error("Failed to load .proto file:", err);
    }
  }

  public send(
    main: { type: string; value: string },
    sub: { type: string; value: string },
    payload?: { protoName?: string | undefined; data?: any | undefined }
  ): void {
    const mainOpc = this.getProtobufEnumValue(main.type, main.value);
    const subOpc = this.getProtobufEnumValue(sub.type, sub.value);
    this.directSend(mainOpc, subOpc, payload?.protoName, payload?.data);
  }

  public directSend(
    main: number,
    sub: number,
    protoName?: string | undefined,
    obj?: any | undefined
  ): void {
    let msg: any;
    if (protoName !== undefined && obj !== undefined) {
      msg = this.encodeProtobuf(protoName, obj);
    }
    const encodedMessage = this.codec.encode(main, sub, msg);
    this.doSend(encodedMessage.buffer as ArrayBuffer);
    //this.doSend(this.codec.encode(main, sub, msg));
  }

  // 新增的 sendData 函数
  public sendData(
    main: number,
    sub: number,
    payload?: { protoName?: string; data?: any }
  ): void {
    if (payload && payload.protoName && payload.data) {
      // 传入了 protoName 和 data，直接进行序列化和发送
      this.directSend(main, sub, payload.protoName, payload.data);
    } else if (payload && payload.data) {
      // 如果只传递了 data，尝试从 data 中推断 protoName
      const protoName = payload.data.$type?.fullName;
      if (protoName) {
        this.directSend(main, sub, protoName, payload.data);
      } else {
        console.error(
          "Failed to determine protoName from data object",
          payload.data
        );
        throw new Error("ProtoName could not be determined from provided data");
      }
    } else {
      // 如果没有传递 payload 或者没有数据，仅发送主命令和子命令
      this.directSend(main, sub);
    }
  }

  public close(code?: number, reason?: string): void {
    this.closedByUser = true;
    this.websocket?.close(code, reason);
  }

  public addMessageListener(
    main: { type: string; value: string },
    sub: { type: string; value: string },
    listener: {
      handler: (instance: WebsocketEx, ev: any) => any;
      protoName?: string | undefined;
    }
  ) {
    const mainOpc = this.getProtobufEnumValue(main.type, main.value);
    const subOpc = this.getProtobufEnumValue(sub.type, sub.value);

    this.directAddMessageListener(
      mainOpc,
      subOpc,
      listener.handler,
      listener.protoName
    );
  }

  public directAddMessageListener(
    main: number,
    sub: number,
    listener: (instance: WebsocketEx, ev: any) => any,
    protoName?: string | undefined
  ) {
    const opc = this.getMessageListenerKey(main, sub);

    let messageListeners = this.messageListeners.get(opc);
    if (messageListeners === undefined) {
      messageListeners = [] as WebsocketMessageListener[];
      this.messageListeners.set(opc, messageListeners);
    }

    const messageListener = {
      listener,
      proto:
        protoName === undefined ? undefined : this.getProtobufType(protoName),
    };
    messageListeners?.push(messageListener);
    //console.log(this.messageListeners, messageListeners);
  }

  public addEventListener<K extends WebsocketEvents>(
    type: K,
    listener: (instance: WebsocketEx, ev: WebsocketEventMap[K]) => any,
    options?: boolean | AddEventListenerOptions
  ): void {
    const eventListener = { listener, options } as eventListener<K>;
    const eventListeners = this.eventListeners[type] as eventListener<K>[];
    eventListeners.push(eventListener);
  }

  public removeEventListener<K extends WebsocketEvents>(
    type: K,
    listener: (instance: WebsocketEx, ev: WebsocketEventMap[K]) => any,
    options?: boolean | EventListenerOptions
  ): void {
    (this.eventListeners[type] as eventListener<K>[]) = (
      this.eventListeners[type] as eventListener<K>[]
    ).filter((l) => {
      return (
        l.listener !== listener &&
        (l.options === undefined || l.options !== options)
      );
    });
  }

  private doSend(data: ArrayBuffer): void {
    if (this.closedByUser) {
      return;
    }
    this.websocket?.send(data);
  }

  public reconnect() {
    this.tryConnect();
  }

  private tryConnect(): void {
    if (this.websocket !== undefined) {
      this.websocket.removeEventListener(
        WebsocketEvents.open,
        this.handleOpenEvent
      );
      this.websocket.removeEventListener(
        WebsocketEvents.close,
        this.handleCloseEvent
      );
      this.websocket.removeEventListener(
        WebsocketEvents.error,
        this.handleErrorEvent
      );
      this.websocket.removeEventListener("message", this.handleMessageEvent);
      this.websocket.close();
    }

    this.websocket = new WebSocket(this.url);
    this.websocket.binaryType = "arraybuffer";

    this.websocket.addEventListener(WebsocketEvents.open, this.handleOpenEvent);
    this.websocket.addEventListener(
      WebsocketEvents.close,
      this.handleCloseEvent
    );
    this.websocket.addEventListener(
      WebsocketEvents.error,
      this.handleErrorEvent
    );
    this.websocket.addEventListener("message", this.handleMessageEvent);

    console.log("websocket--init");
  }

  private handleOpenEvent = (evt: Event) =>
    this.handleEvent(WebsocketEvents.open, evt);

  private handleCloseEvent = (evt: CloseEvent) =>
    this.handleEvent(WebsocketEvents.close, evt);

  private handleErrorEvent = (evt: Event) =>
    this.handleEvent(WebsocketEvents.error, evt);

  private handleMessageEvent = (evt: MessageEvent) =>
    this.doHandleMessageEvent(evt);

  private doHandleMessageEvent(evt: MessageEvent) {
    const msg = this.codec.decode(evt.data);
    if (!msg) {
      console.error("Failed to decode message");
      return;
    }
    this.dispatchMessage(msg);
  }

  private getMessageListenerKey(main: number, sub: number): string {
    return main.toString() + "." + sub.toString();
  }

  private dispatchMessage(evt: WebsocketMessageEvent) {
    //console.log("dispatch message: ", evt);
    const opc = this.getMessageListenerKey(evt.main, evt.sub);
    const messageListeners = this.messageListeners.get(opc);
    if (messageListeners === undefined) {
      console.log("消息未注册,命令-->", opc);
      return;
    }
    messageListeners.forEach((l) => {
      let msg: any;
      if (evt.payload !== undefined && l.proto !== undefined) {
        const buf = protobufjs.util.newBuffer(evt.payload);
        msg = l.proto.decode(buf);
      }
      l.listener(this, msg);
    });
  }

  private handleEvent<K extends WebsocketEvents>(
    type: K,
    evt: WebsocketEventMap[K]
  ) {
    switch (type) {
      case WebsocketEvents.close:
        if (!this.closedByUser) {
          this.reconnect();
        }
        break;
      case WebsocketEvents.open:
        this.retries = 0;
        break;
    }
    this.dispatchEvent<K>(type, evt);
  }

  private dispatchEvent<K extends WebsocketEvents>(
    type: K,
    ev: WebsocketEventMap[K]
  ) {
    const listeners = this.eventListeners[type] as eventListener<K>[];
    if (listeners === undefined) {
      console.log("dispatch event, listeners doesnt exist.", type, listeners);
      return;
    }

    const onceListeners = [] as eventListener<K>[];
    listeners.forEach((l) => {
      l.listener(this, ev);
      if (
        l.options !== undefined &&
        (l.options as AddEventListenerOptions).once
      ) {
        onceListeners.push(l);
      }
    });
    onceListeners.forEach((l) =>
      this.removeEventListener(type, l.listener, l.options)
    );
  }

  private encodeProtobuf(protoName: string, obj: any) {
    const pbConstruct = this.getProtobufType(protoName);
    return pbConstruct?.encode(obj).finish();
  }

  private getProtobufType(typePath: string): any {
    //console.log("ProtoRoot:", this.protoRoot);
    //console.log("Type Requested:", typePath);

    // 解析路径以访问嵌套属性
    const typeParts = typePath.split(".");
    let Type: any = this.protoRoot;

    for (const part of typeParts) {
      Type = Type[part];
      if (!Type) {
        console.error(`Type '${typePath}' not found.`);
        throw new Error(`Type '${typePath}' not found.`);
      }
    }

    return Type;
  }

  private getProtobufEnumValue(
    enumTypePath: string,
    valueName: string
  ): number {
    //console.log("ProtoRoot:", this.protoRoot);
    //console.log("Enum Type Requested:", enumTypePath);

    // 解析路径以访问嵌套属性
    const enumTypeParts = enumTypePath.split(".");
    let EnumType: any = this.protoRoot;

    for (const part of enumTypeParts) {
      EnumType = EnumType[part];
      if (!EnumType) {
        console.error(`Enum type '${enumTypePath}' not found.`);
        throw new Error(`Enum type '${enumTypePath}' not found.`);
      }
    }

    // 获取枚举的值
    const value = EnumType[valueName];
    if (value === undefined) {
      console.error(
        `Value '${valueName}' not found in enum '${enumTypePath}'.`
      );
      throw new Error(
        `Value '${valueName}' not found in enum '${enumTypePath}'.`
      );
    }

    return value;
  }
}
